import React from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { MyTimeWorkingFrameBox } from "my-time/components";
import {
  useMyTimeAdmission,
  useMyTimePeriod,
  useMyTimeRecordingPeriod,
} from "my-time/hooks";
import { countDailyHours, getDateRangeArray } from "my-time/utils";

type MyTimeOvertimeFrameProps = {
  contractCode: string;
  year: number;
  month: number;
  minHeight?: number;
};

export const MyTimeOvertimeFrame: React.FC<MyTimeOvertimeFrameProps> = ({
  contractCode,
  year,
  month,
  minHeight,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const weeklyData = "weekly";

  const { data, loading } = useMyTimePeriod({
    contractCode,
    year: year,
    month: month,
  });

  const recordingPeriod = useMyTimeRecordingPeriod();

  const { data: admissionData } = useMyTimeAdmission({
    contractCode,
    year,
    month,
    changeMyTimeEnabled: true,
    recordingPeriod,
    isApproved: undefined, // or set to a boolean value as needed
  });

  let startPeriod = moment(data?.startDate).startOf("month").toDate();
  let endPeriod = moment(data?.endDate).endOf("month").toDate();

  const dates = getDateRangeArray(
    startPeriod.toISOString(),
    endPeriod.toISOString(),
  );
  let rawOvertime = 0;
  let rawVoluntaryOvertime = 0;

  dates.forEach((date) => {
    const result = countDailyHours({
      date: new Date(date),
      list: admissionData,
    });
    rawOvertime += result?.rawOvertime;
    rawVoluntaryOvertime += result?.rawVoluntaryOvertime;
  });

  const overtimeData = {
    allLabel: t("MY_TIME_OVERTIME"),
    startDate: data?.startDate,
    endDate: data?.endDate,
    hoursByLaw: weeklyData ? 8 : 32,
    hoursByFact: rawOvertime / 60,
  };

  const voluntaryOvertimeData = {
    allLabel: t("MY_TIME_VOLUNTARY_OVERTIME"),
    startDate: data?.startDate,
    endDate: data?.endDate,
    hoursByLaw: 12,
    hoursByFact: rawVoluntaryOvertime / 60,
  };

  const formatValue = (value: number | undefined) => {
    if (value === undefined) {
      return "";
    }
    if (value % 1 !== 0) {
      const formattedValue = value.toFixed(3);
      return formattedValue.endsWith("00")
        ? Number.parseFloat(formattedValue).toFixed(1)
        : formattedValue;
    } else {
      return value.toString();
    }
  };

  const overtimeValue = formatValue(overtimeData?.hoursByFact);

  return (
    <Box>
      <MyTimeWorkingFrameBox
        label={t("MY_TIME_VOLUNTARY_OVERTIME")}
        value={formatValue(voluntaryOvertimeData?.hoursByFact)}
        color={theme.palette.primary.main}
        loading={loading || !data}
        overtimeValue={overtimeValue}
        typeName="frameOvertime"
        minHeight={minHeight}
      />
    </Box>
  );
};

export default MyTimeOvertimeFrame;
